<template>
  <v-card v-if="['A'].find(el=>el==store.state.user.groupe)">
    <v-card-title class="d-flex align-center flex-wrap ga-2">
      <span>Sous-familles</span>
      <v-chip v-if="dirty" color="warning" size="small" variant="tonal">Modifications non enregistrées</v-chip>
      <v-spacer />
      
    </v-card-title>

    <v-card-text>
      <v-row dense>
         <v-col cols="12" md="3">
          <v-select
            v-model="filtreClassification"
            :items="rayons"
            variant="outlined"
            label="Rayon"
            clearable
            hide-details
            density="compact"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="recherche"
            variant="outlined"
            label="Rechercher (code ou libellé)"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            density="compact"
          />
        </v-col>
       
        <v-col cols="12" md="3">
            <v-btn color="primary"  prepend-icon="mdi-plus" @click="ouvrirAjout()">Ajouter</v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="lignesFiltrees"
      :items-per-page="25"
      item-value="code"
      density="compact"
      hover
    >
      <template #item.code="{ item }">
        <strong>{{ item.code }}</strong>
      </template>

      <template #item.actions="{ item }">
        <v-btn icon="mdi-pencil" size="x-small" variant="text" @click="ouvrirEdition(item)" />
        <!-- <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="demanderSuppression(item)" /> -->
      </template>

      <template #no-data>Aucune sous-famille trouvée.</template>
    </v-data-table>

    <v-divider />

    <v-card-actions>
      <v-btn prepend-icon="mdi-download" variant="text" @click="telechargerJson">Exporter JSON</v-btn>
      <v-spacer />
      <v-btn :disabled="!dirty" variant="text" @click="annulerTout">Annuler les modifications</v-btn>
      <v-btn :disabled="!dirty" color="primary" variant="flat" @click="enregistrer">Enregistrer</v-btn>
    </v-card-actions>

    <!-- Formulaire ajout / modification -->
    <v-dialog v-model="dialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ modeEdition ? `Modifier ${form.code}` : 'Nouvelle sous-famille' }}</v-card-title>
        <v-card-text>
          <v-form ref="formRef" @submit.prevent="validerFormulaire">
            <v-row dense>
              <v-col cols="8">
                <v-text-field
                  :model-value="form.code"
                  label="Code"
                  :disabled="modeEdition"
                  :rules="modeEdition ? [] : reglesCode"
                  maxlength="3"
                  hint="Format : lettre + chiffre + lettre (ex. J4L)"
                  persistent-hint
                  @update:model-value="v => (form.code = (v || '').toUpperCase())"
                />
              </v-col>
              <v-col v-if="!modeEdition" cols="4" class="d-flex align-center">
                <v-btn
                  variant="tonal"
                  size="small"
                  :disabled="!/^[A-Z][0-9]$/.test(form.code.slice(0, 2))"
                  @click="suggererCode"
                >
                  Code suivant
                </v-btn>
              </v-col>
              <v-col v-for="l in langues" :key="l.key" cols="12">
                <v-text-field v-model="form[l.key]" :label="l.label" :rules="[requis]" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">Annuler</v-btn>
          <v-btn color="primary" variant="flat" @click="validerFormulaire">
            {{ modeEdition ? 'Modifier' : 'Ajouter' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation suppression -->
    <v-dialog v-model="dialogSuppression" max-width="420">
      <v-card>
        <v-card-title>Supprimer {{ aSupprimer?.code }} ?</v-card-title>
        <v-card-text>« {{ aSupprimer?.fr }} » sera retiré de la liste.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogSuppression = false">Annuler</v-btn>
          <v-btn color="error" variant="flat" @click="confirmerSuppression">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="2500">
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import store from '@/store'
/**
 * Utilisation :
 *   <SousFamillesEditor v-model="sousFamilles" @save="sauvegarder" />
 * modelValue : { "J1A": { fr, nl, en }, ... }
 * L'objet n'est renvoyé au parent qu'au clic sur « Enregistrer ».
 */
// const props = defineProps({
//   modelValue: { type: Object, required: true },
// })
// const emit = defineEmits(['update:modelValue', 'save'])



const langues = [
  { key: 'fr', label: 'Libellé français' },
  { key: 'nl', label: 'Libellé néerlandais' },
  { key: 'en', label: 'Libellé anglais' },
]

const headers = [
  { title: 'Code', key: 'code', width: 90 },
  { title: 'Français', key: 'fr' },
  { title: 'Néerlandais', key: 'nl' },
  { title: 'Anglais', key: 'en' },
  { title: '', key: 'actions', sortable: false, align: 'end', width: 110 },
]

// ---- Copie de travail (le parent n'est modifié qu'à l'enregistrement) ----
const copie = obj => JSON.parse(JSON.stringify(obj || {}))
const donnees = ref(copie(store.state.sousFamillesObj)) // copie de travail, modifiée par l'utilisateur
const dirty = ref(false)

watch(
  () => store.state.sousFamillesObj,
  v => {
    donnees.value = copie(v)
    dirty.value = false
  },
  { deep: true }
)

const lignes = computed(() =>
  Object.entries(donnees.value).map(([code, v]) => ({ code, fr: v.fr, nl: v.nl, en: v.en }))
)

// ---- Recherche et filtre ----
const recherche = ref('')
const filtreClassification = ref(null)

const rayons = computed(() => [...new Set(Object.keys(donnees.value).map(c => c[0]))])

const sansAccents = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const lignesFiltrees = computed(() => {
  const q = sansAccents(recherche.value)
  return lignes.value.filter(l => {
    if (filtreClassification.value && l.code[0] !== filtreClassification.value) return false
    if (!q) return true
    return [l.code, l.fr, l.nl, l.en].some(t => sansAccents(t).includes(q))
  })
})

// ---- Formulaire ----
const dialog = ref(false)
const modeEdition = ref(false)
const formRef = ref(null)
const form = ref({ code: '', fr: '', nl: '', en: '' })

const requis = v => !!(v && v.trim()) || 'Champ obligatoire'
const reglesCode = [
  requis,
  v => /^[A-Z][0-9][A-Z]$/.test(v) || 'Format attendu : lettre + chiffre + lettre',
  v => !donnees.value[v] || 'Ce code existe déjà',
]

function ouvrirAjout() {
  modeEdition.value = false
  form.value = { code: filtreClassification.value || '', fr: '', nl: '', en: '' }
  dialog.value = true
}

function ouvrirEdition(item) {
  modeEdition.value = true
  form.value = { ...item }
  dialog.value = true
}

/** Propose la première lettre libre après le préfixe saisi (ex. J4 -> J4L). */
function suggererCode() {
  const prefixe = form.value.code.slice(0, 2)
  for (let c = 65; c <= 90; c++) {
    const code = prefixe + String.fromCharCode(c)
    if (!donnees.value[code]) {
      form.value.code = code
      return
    }
  }
  notifier(`Plus de code libre pour ${prefixe}`, 'error')
}

const majuscule = s => {
  s = (s || '').trim()
  return s.charAt(0).toLocaleUpperCase('fr') + s.slice(1)
}

/**
 * Insère la nouvelle clé à sa place dans son pôle, sans changer l'ordre des pôles :
 * après la dernière clé du même pôle qui la précède, sinon avant la première clé
 * du pôle, sinon à la fin (nouveau pôle).
 */
function insererTrie(obj, code, valeur) {
  const cles = Object.keys(obj)
  let apres = -1
  let premier = -1
  cles.forEach((k, i) => {
    if (k[0] !== code[0]) return
    if (premier === -1) premier = i
    if (k < code) apres = i
  })
  const position = apres !== -1 ? apres + 1 : premier !== -1 ? premier : cles.length
  const resultat = {}
  cles.forEach((k, i) => {
    if (i === position) resultat[code] = valeur
    resultat[k] = obj[k]
  })
  if (position === cles.length) resultat[code] = valeur
  return resultat
}

async function validerFormulaire() {
  const { valid } = await formRef.value.validate()
  if (!valid) return

  const { code } = form.value
  const valeur = { fr: majuscule(form.value.fr), nl: majuscule(form.value.nl), en: majuscule(form.value.en) }

  if (modeEdition.value) {
    donnees.value[code] = valeur
    notifier(`${code} modifié`)
  } else {
    donnees.value = insererTrie(donnees.value, code, valeur)
    notifier(`${code} ajouté`)
  }
  dirty.value = true
  dialog.value = false
}

// ---- Suppression ----
const dialogSuppression = ref(false)
const aSupprimer = ref(null)

function demanderSuppression(item) {
  aSupprimer.value = item
  dialogSuppression.value = true
}

function confirmerSuppression() {
  const { code } = aSupprimer.value
  const { [code]: _, ...reste } = donnees.value
  donnees.value = reste
  dirty.value = true
  dialogSuppression.value = false
  notifier(`${code} supprimé`)
}

// ---- Enregistrement / export ----
function enregistrer() {
  const resultat = copie(donnees.value)
  emit('update:modelValue', resultat)
  emit('save', resultat)
  dirty.value = false
  notifier('Modifications enregistrées')
}

function annulerTout() {
  donnees.value = copie(store.state.sousFamillesObj,)
  dirty.value = false
}

function telechargerJson() {
  const lignesJson = Object.entries(donnees.value).map(([k, v]) => `    "${k}":${JSON.stringify(v)}`)
  const blob = new Blob(['{\n' + lignesJson.join(',\n') + '\n}\n'], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'sousfamilles.json'
  a.click()
  URL.revokeObjectURL(url)
}

// ---- Notifications ----
const snackbar = ref({ show: false, text: '', color: 'success' })
function notifier(text, color = 'success') {
  snackbar.value = { show: true, text, color }
}
</script>